import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from "@angular/core";
import {
    UntypedFormGroup,
    UntypedFormBuilder,
    Validators
  } from "@angular/forms";
  import { DataProvider } from "projects/packcert-web/src/app/providers/data.provider";
  import { AdminService } from '../../../services/admin.service';
  import { AdminContextService } from '../../../context/admin-contex.service';
  import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';

  import { SpinnerService, Message, UtilsService } from "shared-components";
  import { Subject } from "rxjs";
  import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-not-new-edit',
    templateUrl: './new-edit.component.html'
})
export class NotNewEditComponent implements OnInit, OnDestroy {
    public newEditForm: UntypedFormGroup;

    public paises;
    public denominaciones;
    public roles;
    public entidades;
    public etiquetadores;

    public disabledEntidades: Subject<boolean> = new Subject();
    public disabledEtiquetadores: Subject<boolean> = new Subject();

    public initialStatePaises = "noSelected";
    public initialStateDenominaciones = "noSelected";
    public initialStateRoles = "noSelected";
    public initialStateEntidades = "noSelected";
    public initialStateEtiquetadores = "noSelected";

    public showError: boolean;
    public mensajes = new Array<Message>();

    private filters;

    // Marcar las opciones que correspondan al recibir la respuesta
    public paisSpecificSelection = [];
    public denominacionSpecificSelection = [];
    public rolSpecificSelection = [];
    public entidadSpecificSelection = [];
    public etiquetadorSpecificSelection = [];

    public showPaisError = false;
    public showDenominacionError = false;
    public showRolError = false;
    public showEntidadError = false;
    public showEtiquetadorError = false;
    public showFechaError = false;

    private validDate = true;
    private firstTimeFilter = [true, true, true] // Para evitar hacer peticiones triples cuando se abre el modal de editar y se autocompleta con los datos guardados

    private readonly destroy$ = new Subject<void>();

    @Input() notificationId: number;
    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    constructor(
        private _fb: UntypedFormBuilder,
        private _dataProvider: DataProvider,
        private _adminService: AdminService,
        private _adminContext: AdminContextService,
        private _sessionContext: SessionContextService,
        private _spinnerService: SpinnerService,
        private utilsService: UtilsService
    ) {}

    ngOnInit(): void {
        this.newEditForm = this._fb.group({
            pais: ["", Validators.required],
            denominacion: ["", Validators.required],
            rol: ["", Validators.required], // Enviar un 1 a la db si se marcan todos
            entidad: [{ value: "", disabled: true }],
            etiquetador: [{ value: "", disabled: true }],
            titulo: ["", Validators.required],
            detalle: ["", Validators.required],
            fechaEnvio: [""],
        })

        this.paises = this._dataProvider.paises;
        this.denominaciones = this._dataProvider.denominaciones;
        this.roles = this._dataProvider.rolesNotificacion;
        this.entidades = [{}];
        this.etiquetadores = [{}];

        // Si se va a editar la notificación
        if (this.notificationId) {
            this._spinnerService.showSpinner();
            this._adminService.getNotificacion(this.notificationId).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                this.newEditForm.patchValue({
                    pais: resp.lsPaisIds,
                    denominacion: resp.lsDenominacionIds,
                    rol: resp.rolDestinatarioId === 1 ? [2, 3] : [resp.rolDestinatarioId],
                    entidad: resp.lsEntidadControlIds,
                    etiquetador: resp.lsEtiquetadorIds,
                    titulo: resp.titulo,
                    detalle: resp.notificacion,
                    fechaEnvio: resp.fechaEnvio
                });

                this.paisSpecificSelection = resp.lsPaisIds;
                this.denominacionSpecificSelection = resp.lsDenominacionIds;
                this.rolSpecificSelection = resp.rolDestinatarioId === 1 ? [2, 3] : [resp.rolDestinatarioId];
            })
        }

        // Comprobamos que se hayan rellenado los 3 campos "país", "denominación" y "rol"
        this.newEditForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(pais => {
            if (pais && this.newEditForm.get('denominacion').value && this.newEditForm.get('rol').value) {
                
                if (this.notificationId && this.firstTimeFilter[0]) {
                    this.firstTimeFilter.splice(0, 1, false);
                    return;
                }

                if (this.newEditForm.get('rol').value?.includes(2)) {
                    const filters = {
                        lsPaisIds: this.newEditForm.get('pais').value,
                        lsDenominacionIds: this.newEditForm.get('denominacion').value
                    };
                    this._spinnerService.showSpinner();
                    this._adminService.getEntidadesControl(filters).toPromise().finally( () => {
                        if (this.notificationId) {
                            this.entidadSpecificSelection = this.newEditForm.get('entidad').value ? this.newEditForm.get('entidad').value : [];
                        }
                        this._spinnerService.hideSpinner();
                    }).then(resp => {
                        this.entidades = resp.lista;
                        // Para, desde el componente multiselect, bloquear el campo y asignarle automáticamente el valor cuando solo hay 1 elemento
                        if (resp.lista.length === 1) {
                            this.entidadSpecificSelection = [resp.lista[0].id];
                            // Habilitamos el campo solo si hay una respuesta válida
                        } else if (resp.lista.length > 1) {
                            this.disabledEntidades.next(false);
                        }
                    })
                }
                if (this.newEditForm.get('rol').value?.includes(3)) {
                    const filters = {
                        setPD_Cuando_LsPaisIds_LsDenominacionIds_1Item: false,
                        lsPaisIds: this.newEditForm.get('pais').value,
                        lsDenominacionIds: this.newEditForm.get('denominacion').value
                    };
                    this._spinnerService.showSpinner();
                    // La llamada realmente filtra por otros parámetros
                    this._adminService.getEtiquetadoresByEntidadControl(filters).toPromise().finally(() => {
                        if (this.notificationId) {
                            this.etiquetadorSpecificSelection = this.newEditForm.get('etiquetador').value ? this.newEditForm.get('etiquetador').value : [];
                        }
                        this._spinnerService.hideSpinner();
                    }).then((resp) => {
                        this.etiquetadores = resp.lista;
                        if (resp.lista.length === 1) {
                            this.etiquetadorSpecificSelection = [resp.lista[0].id];
                        } else if (resp.lista.length > 1) {
                            this.disabledEtiquetadores.next(false);
                        }
                    });
                }
            } else {
                this.disabledEntidades.next(true);
                this.disabledEtiquetadores.next(true);
            }

            if (!pais) {
                this.entidadSpecificSelection = [];
                this.etiquetadorSpecificSelection = [];
                this.newEditForm.get('entidad').patchValue("");
                this.newEditForm.get('etiquetador').patchValue("");
            }
        });

        this.newEditForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(denominacion => {
            if (denominacion && this.newEditForm.get('pais').value && this.newEditForm.get('rol').value) {

                if (this.notificationId && this.firstTimeFilter[1]) {
                    this.firstTimeFilter.splice(1, 1, false);
                    return;
                }

                if (this.newEditForm.get('rol').value?.includes(2)) {
                    const filters = {
                        lsPaisIds: this.newEditForm.get('pais').value,
                        lsDenominacionIds: this.newEditForm.get('denominacion').value
                    };
                    this._spinnerService.showSpinner();
                    this._adminService.getEntidadesControl(filters).toPromise().finally( () => {
                        if (this.notificationId) {
                            this.entidadSpecificSelection = this.newEditForm.get('entidad').value ? this.newEditForm.get('entidad').value : [];
                        }
                        this._spinnerService.hideSpinner();
                    }).then(resp => {
                        this.entidades = resp.lista;
                        if (resp.lista.length === 1) {
                            this.entidadSpecificSelection = [resp.lista[0].id];
                        } else if (resp.lista.length > 1) {
                            this.disabledEntidades.next(false);
                        }
                    })
                }
                if (this.newEditForm.get('rol').value?.includes(3)) {
                    const filters = {
                        setPD_Cuando_LsPaisIds_LsDenominacionIds_1Item: false,
                        lsPaisIds: this.newEditForm.get('pais').value,
                        lsDenominacionIds: this.newEditForm.get('denominacion').value
                    };
                    this._spinnerService.showSpinner();
                    // La llamada realmente filtra por otros parámetros
                    this._adminService.getEtiquetadoresByEntidadControl(filters).toPromise().finally(() => {
                        if (this.notificationId) {
                            this.etiquetadorSpecificSelection = this.newEditForm.get('etiquetador').value ? this.newEditForm.get('etiquetador').value : [];
                        }
                        this._spinnerService.hideSpinner();
                    }).then((resp) => {
                        this.etiquetadores = resp.lista;
                        if (resp.lista.length === 1) {
                            this.etiquetadorSpecificSelection = [resp.lista[0].id];
                        } else if (resp.lista.length > 1) {
                            this.disabledEtiquetadores.next(false);
                        }
                    });
                }
            } else {
                this.disabledEntidades.next(true);
                this.disabledEtiquetadores.next(true);
            }

            if (!denominacion) {
                this.entidadSpecificSelection = [];
                this.etiquetadorSpecificSelection = [];
                this.newEditForm.get('entidad').patchValue("");
                this.newEditForm.get('etiquetador').patchValue("");
            }
        });

        this.newEditForm.get('rol').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(rol => {
            
            if (this.notificationId && this.firstTimeFilter[2]) {
                this.firstTimeFilter.splice(2, 1, false);
                return;
            }

            if (rol && this.newEditForm.get('pais').value && this.newEditForm.get('denominacion').value) {
                if (rol.includes(2)) {
                    const filters = {
                        lsPaisIds: this.newEditForm.get('pais').value,
                        lsDenominacionIds: this.newEditForm.get('denominacion').value
                    };
                    this._spinnerService.showSpinner();
                    this._adminService.getEntidadesControl(filters).toPromise().finally( () => {
                        this._spinnerService.hideSpinner();
                        if (this.notificationId) {
                            this.entidadSpecificSelection = this.newEditForm.get('entidad').value ? this.newEditForm.get('entidad').value : [];
                        }
                    }).then(resp => {
                        this.entidades = resp.lista;
                        if (resp.lista.length > 1) {
                            this.disabledEntidades.next(false);
                        } else if (resp.lista.length === 1) {
                            this.entidadSpecificSelection = [resp.lista[0].id];
                        }
                    })
                } else if (!rol.includes(2)) {
                    this.disabledEntidades.next(true);
                }

                if (rol.includes(3)) {
                    const filters = {
                        setPD_Cuando_LsPaisIds_LsDenominacionIds_1Item: false,
                        lsPaisIds: this.newEditForm.get('pais').value,
                        lsDenominacionIds: this.newEditForm.get('denominacion').value
                    };
                    this._spinnerService.showSpinner();
                    // La llamada realmente filtra por otros parámetros
                    this._adminService.getEtiquetadoresByEntidadControl(filters).toPromise().finally(() => {
                        if (this.notificationId) {
                            this.etiquetadorSpecificSelection = this.newEditForm.get('etiquetador').value ? this.newEditForm.get('etiquetador').value : [];
                        }
                        this._spinnerService.hideSpinner();
                    }).then((resp) => {
                        this.etiquetadores = resp.lista;
                        if (resp.lista.length > 1) {
                            this.disabledEtiquetadores.next(false);
                        } else if (resp.lista.length === 1) {
                            this.etiquetadorSpecificSelection = [resp.lista[0].id];
                        }
                    });
                } else if (!rol.includes(3)) {
                    this.disabledEtiquetadores.next(true);
                }
            } else {
                this.disabledEntidades.next(true);
                this.disabledEtiquetadores.next(true);
            }

            if (!rol || (rol && !rol.includes(3))) {  
                this.etiquetadores = [];             
                this.etiquetadorSpecificSelection = [];
                this.newEditForm.get('etiquetador').patchValue("");
                this.disabledEtiquetadores.next(true);
            }
            if (!rol || (rol && !rol.includes(2))) {
                this.entidades = [];
                this.entidadSpecificSelection = [];
                this.newEditForm.get('entidad').patchValue("");
                this.disabledEntidades.next(true);
            }
        });

        // Comprobar que la fecha de envío sea hoy o posterior a la fecha actual
        this.newEditForm.get('fechaEnvio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(fecha => {
            const currentDate = new Date().setHours(0, 0, 0, 0); // Reseteamos la hora para que tome como válido el día de hoy
            const dateToCompare = new Date (fecha).setHours(0, 0, 0, 0);
            if (dateToCompare < currentDate) {
                this.validDate = false;
            } else {
                this.validDate = true;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public handleApiErrors(condition: any) {
        switch (condition) {
            case 6221:
                this.mensaje.emit('centralNotificaciones.errors.noAccesible');
                break;
            case 6222:
                this.mensaje.emit('centralNotificaciones.errors.sinTitulo');
                break;
            case 6223:
                this.mensaje.emit('centralNotificaciones.errors.sinDestinatarios');
                break;
            case 6224:
                this.mensaje.emit('centralNotificaciones.errors.fechaEnvio');
                break;
            case 6225:
                this.mensaje.emit('centralNotificaciones.errors.usuario');
                break;
            default:
                this.mensaje.emit('global.error.noResponse');
        }
        this.refresh.emit(false);
    }

    private applyFilters() {
        this._adminContext.notificacionFilters = {
            lsPaisIds: this.newEditForm.get('pais').value ? this.newEditForm.get('pais').value : null,
            lsDenominacionIds: this.newEditForm.get('denominacion').value ? this.newEditForm.get('denominacion').value : null,
            rolDestinatarioId: this.newEditForm.get('rol').value && this.newEditForm.get('rol').value.length > 1 ? 1 : this.newEditForm.get('rol').value[0],
            lsEntidadControlIds: this.newEditForm.get('entidad').value ? this.newEditForm.get('entidad').value : null,
            lsEtiquetadorIds: this.newEditForm.get('etiquetador').value ? this.newEditForm.get('etiquetador').value : null,
            fechaEnvio: this.newEditForm.get('fechaEnvio').value && this.newEditForm.get('fechaEnvio').value instanceof Date ? this.utilsService.parseGMTtoYYYYMMDD(this.newEditForm.get('fechaEnvio').value) : this.newEditForm.get('fechaEnvio').value && this.newEditForm.get('fechaEnvio').value !== "" ? this.newEditForm.get('fechaEnvio').value : '',
            titulo: this.newEditForm.get('titulo').value ? this.newEditForm.get('titulo').value : null,
            notificacion: this.newEditForm.get('detalle').value ? this.newEditForm.get('detalle').value : null,
        }
    }

    public updateNotification() {
        this._spinnerService.showSpinner();
        this._adminService.updateNotificacion(this.filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.refresh.emit(true);
            this.mensaje.emit('global.success.itemUpdated');
        }, (error) => {
            if (error.status === 420) {
                this.handleApiErrors(error.error.ApiErrorCode);
            } else {
                this.refresh.emit(false);
                this.mensaje.emit('global.error.noResponse');
            }
        });
    }

    public createNotification() {
        this._spinnerService.showSpinner();
        this._adminService.createNotificacion(this.filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.refresh.emit(true);
            this.mensaje.emit('global.success.itemCreated');
        }, (error) => {
            if (error.status === 420) {
                this.handleApiErrors(error.error.ApiErrorCode);
            } else {
                this.refresh.emit(false);
                this.mensaje.emit('global.error.noResponse');
            }
        });
    }

    private showErrors() {
        this.showError = true;
        if (!this.newEditForm.get('pais').value || (this.newEditForm.get('pais').value && this.newEditForm.get('pais').value.length === 0)) {
            this.showPaisError = true;
            if (this.mensajes.findIndex(object=> object.message === 'global.error.required') === -1) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
            setTimeout(() => {
                this.showPaisError = false;
            }, 5300);
        }
        if (!this.newEditForm.get('denominacion').value || (this.newEditForm.get('denominacion').value && this.newEditForm.get('denominacion').value.length === 0)) {
            this.showDenominacionError = true;
            if (this.mensajes.findIndex(object=> object.message === 'global.error.required') === -1) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
            setTimeout(() => {
                this.showDenominacionError = false;
            }, 5300);
        }
        if (!this.newEditForm.get('rol').value || (this.newEditForm.get('rol').value && this.newEditForm.get('rol').value.length === 0)) {
            this.showRolError = true;
            if (this.mensajes.findIndex(object=> object.message === 'global.error.required') === -1) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
            setTimeout(() => {
                this.showRolError = false;
            }, 5300);
        }
        if (this.newEditForm.get('rol').value && this.newEditForm.get('rol').value.includes(2) && (this.newEditForm.get('entidad').value === "" || !this.newEditForm.get('entidad').value)) {
            this.showEntidadError = true;
            if (this.mensajes.findIndex(object=> object.message === 'global.error.required') === -1) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
            setTimeout(() => {
                this.showEntidadError = false;
            }, 5300);
        }
        if (this.newEditForm.get('rol').value && this.newEditForm.get('rol').value.includes(3) && (this.newEditForm.get('etiquetador').value === "" || !this.newEditForm.get('etiquetador').value)) {
            this.showEtiquetadorError = true;
            if (this.mensajes.findIndex(object=> object.message === 'global.error.required') === -1) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
            setTimeout(() => {
                this.showEtiquetadorError = false;
            }, 5300);
        }
        if (!this.validDate) {
            this.showFechaError = true;
            setTimeout(() => {
                this.showFechaError = false;
            }, 5300);
            this.mensajes.push({ class: 'error', message: 'centralNotificaciones.errors.fechaEnvio' })
        } else {
            if (this.mensajes.findIndex(object=> object.message === 'global.error.required') === -1) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
        }
    }

    public save() {
        const role = this.newEditForm.get('rol').value;

        /* Si el formulario es válido, comprobamos también que se haya rellenado el campo entidad/etiquetador según corresponda.
        En este caso Angular acepta como válido array vacío/string vacía para los multiselect. */
        if (
          this.newEditForm.valid &&
          this.newEditForm.get("titulo").value &&
          this.newEditForm.get("titulo").value.trim().length !== 0 &&
          this.newEditForm.get("pais").value &&
          this.newEditForm.get("pais").value.length !== 0 &&
          this.newEditForm.get("denominacion").value &&
          this.newEditForm.get("denominacion").value.length !== 0 &&
          this.newEditForm.get("rol").value &&
          this.newEditForm.get("rol").value.length !== 0 &&
          (this.newEditForm.get("fechaEnvio").value === "" ||
            (this.newEditForm.get("fechaEnvio").value !== "" &&
              this.validDate)) &&
          ((role.includes(2) &&
            role.includes(3) &&
            this.newEditForm.get("entidad").value &&
            this.newEditForm.get("entidad").value.length !== 0 &&
            this.newEditForm.get("etiquetador").value &&
            this.newEditForm.get("etiquetador").value.length !== 0) ||
            (role.includes(2) &&
              this.newEditForm.get("entidad").value &&
              this.newEditForm.get("entidad").value.length !== 0) ||
            (role.includes(3) &&
              this.newEditForm.get("etiquetador").value &&
              this.newEditForm.get("etiquetador").value.length !== 0))
        ) {
          this.applyFilters();
          this.filters = this._adminContext.notificacionFilters;

          // Editar
          if (this.notificationId) {
            this.filters.id = this.notificationId;
            this.updateNotification();
            // Crear
          } else {
            this.createNotification();
          }
        } else {
          this.showErrors();
        }
        
    }

    public close() {
        this.refresh.emit(false);
    }

    public shareCheckedListPaises(item: any[]) {
        this.newEditForm.get('pais').patchValue(item);
    }

    public shareCheckedListDenominaciones(item: any[]) {
        this.newEditForm.get('denominacion').patchValue(item);
    }

    public shareCheckedListRoles(item: any[]) {
        this.newEditForm.get('rol').patchValue(item);
    }

    public shareCheckedListEntidades(item: any[]) {
        this.newEditForm.get('entidad').patchValue(item);
    }

    public shareCheckedListEtiquetadores(item: any[]) {
        this.newEditForm.get('etiquetador').patchValue(item);
    }
}